/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import { formatPrice } from "@lemmonhq/orders";
import { FastNumberInput, useLocalization } from "@lemmonhq/uikit";
import { Loading } from "@app/vue/components/index";
import type { DiscountType } from "@lemmonhq/types";
import { useAuth } from "@app/vue/providers/Auth";

interface SavedDiscountGridProps {
  discountTypes: DiscountType[] | undefined;
  isPending: boolean;
  selectedTotal: number;
  disabled: boolean;
  onApply: (discount: DiscountType, overrideValue?: number) => void;
}

export const SavedDiscountGrid = defineComponent<SavedDiscountGridProps>({
  name: "SavedDiscountGrid",
  props: ["discountTypes", "isPending", "selectedTotal", "disabled", "onApply"],
  setup(props) {
    const auth = useAuth();
    const { t } = useLocalization();
    const expandedId = ref<string | null>(null);
    const overrideValue = ref<number | null>(null);

    const expandedDiscount = computed(
      () => props.discountTypes?.find((d) => d.id === expandedId.value) ?? null,
    );

    const discountAmountPreview = computed(() => {
      if (!expandedDiscount.value) return null;
      const val = overrideValue.value ?? expandedDiscount.value.default.value;
      if (expandedDiscount.value.default.type === "percentage") {
        return (props.selectedTotal * val) / 100;
      }
      return val;
    });

    const handleCardClick = (discount: DiscountType) => {
      if (props.disabled) return;
      if (!discount.allow_changes) {
        props.onApply(discount);
        return;
      }
      if (expandedId.value === discount.id) {
        expandedId.value = null;
        overrideValue.value = null;
      } else {
        expandedId.value = discount.id;
        overrideValue.value = null;
      }
    };

    const handleApplyExpanded = () => {
      if (!expandedDiscount.value) return;
      props.onApply(expandedDiscount.value, overrideValue.value ?? undefined);
      expandedId.value = null;
      overrideValue.value = null;
    };

    return () => {
      if (props.isPending) {
        return (
          <div class="flex justify-center py-10">
            <Loading />
          </div>
        );
      }

      if (!props.discountTypes || props.discountTypes.length === 0) {
        return (
          <div class="flex flex-col items-center justify-center rounded-xl border border-base-content/10 bg-base-200/40 py-10 text-center">
            <span class="material-symbols-outlined mb-2 text-4xl text-base-content/25">
              local_offer
            </span>
            <p class="text-sm text-base-content/50">
              {t("settings.discount_types_page.empty_description")}
            </p>
          </div>
        );
      }

      return (
        <div class="space-y-2">
          <div class="grid grid-cols-2 gap-2">
            {props.discountTypes.map((discount) => {
              const isExpanded = expandedId.value === discount.id;
              return (
                <button
                  key={discount.id}
                  type="button"
                  class={`rounded-xl l-box-shadow border p-3 text-left transition-all ${
                    props.disabled
                      ? "cursor-not-allowed opacity-40 border-base-content/10"
                      : isExpanded
                        ? " bg-primary/8 shadow-sm"
                        : " bg-base-100"
                  }`}
                  onClick={() => handleCardClick(discount)}
                >
                  <div class="flex flex-col gap-2">
                    <div class="flex items-start justify-between gap-1">
                      <span class="text-sm font-semibold leading-tight text-accent">
                        {discount.name}
                      </span>
                      {isExpanded && (
                        <span class="material-symbols-outlined flex-shrink-0 text-[16px] text-primary">
                          check_circle
                        </span>
                      )}
                    </div>
                    <div class="flex items-end justify-between">
                      <span class="text-[10px] uppercase tracking-wide text-base-content/40">
                        {discount.allow_changes
                          ? t("settings.discount_types_page.badge_flexible")
                          : t("settings.discount_types_page.badge_fixed")}
                      </span>
                      <span
                        class={`text-base font-bold ${isExpanded ? "text-primary" : "text-base-content/80"}`}
                      >
                        {discount.default.type === "percentage"
                          ? `${discount.default.value}%`
                          : formatPrice(
                              discount.default.value,
                              true,
                              2,
                              auth.user.value?.restaurant.currency || "CHF",
                            )}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Expanded flexible discount editor */}
          {expandedDiscount.value?.allow_changes && (
            <div class="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-3">
              <div class="text-xs font-semibold uppercase tracking-wide text-primary/70">
                {expandedDiscount.value.name} –{" "}
                {t("pos.product_discount.saved_custom_value")}
              </div>
              <FastNumberInput
                value={overrideValue.value}
                onChange={(v) => (overrideValue.value = v)}
                min={1}
                simplified
                simplifiedLabel={
                  expandedDiscount.value.default.type === "percentage"
                    ? t("pos.product_discount.label_percentage")
                    : t("pos.product_discount.label_amount")
                }
              />
              {discountAmountPreview.value != null &&
                props.selectedTotal > 0 && (
                  <div class="flex items-center justify-between text-xs text-success/80">
                    <span>{t("pos.product_discount.discount_label")}</span>
                    <span class="font-semibold">
                      −
                      {formatPrice(
                        discountAmountPreview.value,
                        true,
                        2,
                        auth.user.value?.restaurant.currency || "CHF",
                      )}
                    </span>
                  </div>
                )}
              <button
                type="button"
                class="btn btn-primary btn-sm w-full rounded-xl"
                onClick={handleApplyExpanded}
              >
                {t("pos.product_discount.saved_apply_button").replace(
                  "{0}",
                  expandedDiscount.value.name,
                )}
              </button>
            </div>
          )}
        </div>
      );
    };
  },
});
